import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import { z } from 'zod';
import FormInput from '@/components/common/form-input';
import ProgressButton from '@/components/common/progress-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import useGroupInfo from '@/hooks/groups/use-group-info';
import useSetGroupNickname from '@/hooks/groups/use-set-group-nickname';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import ROUTES from '@/routes';

export default function UpdateGroupNicknameRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: groupId } = useTypedParams(ROUTES.MESSAGES.GROUPS.SETTINGS.NICKNAME);

  const { data: groupInfo, isLoading: isLoadingGroupInfo } = useGroupInfo(groupId);
  const { mutateAsync: setGroupNickname, isPending: isSettingGroupNickname } = useSetGroupNickname(groupId);

  const {
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { isDirty },
  } = useForm<{ nickname: string }>({
    resolver: zodResolver(updateGroupNicknameFormScheme),
    defaultValues: { nickname: '' },
  });

  useEffect(() => {
    if (groupInfo?.Self) {
      reset({ nickname: groupInfo.Self.GroupMemberName || groupInfo.Self.UserNickName });
    }
  }, [groupInfo?.Self, reset]);

  useAdjustLayout();

  const submit = async (data: UpdateGroupNicknameFormData) => {
    const result = await setGroupNickname(data.nickname);

    if (result.Code === 0) {
      navigate(-1);
    }
  };

  const handleReset = () => {
    if (groupInfo?.Self) {
      setValue('nickname', '', { shouldDirty: true });
      handleSubmit(submit)();
    }
  };

  const isLoading = isLoadingGroupInfo || isSettingGroupNickname;

  return (
    <PageLayout
      title={t('conversation.setting.group.set-nickname')}
      headerConfig={{
        navigateBack: true,
      }}
      className='pb-safe-offset-4'>
      <LoadingLayout promise={isLoading}>
        {() => (
          <form
            onSubmit={handleSubmit(submit)}
            className='mx-5 mt-4 flex flex-1 flex-col justify-between gap-2'>
            <label
              htmlFor='nickname'
              className='form-control w-full'>
              <FormInput
                disabled={isLoading}
                name='nickname'
                control={control}
                defaultValue=''
                maxLength={15}
                helperText={t('fields.messages.max-length', {
                  name: t('conversation.setting.group.nickname'),
                  count: 15,
                })}
              />
            </label>

            <div className='flex flex-col items-stretch justify-end gap-2'>
              {groupInfo?.Self?.GroupMemberName && groupInfo.Self.GroupMemberName !== groupInfo.Self.UserNickName && (
                <ProgressButton
                  as='button'
                  isLoading={isLoading}
                  className='btn btn-ghost'
                  label='common.reset'
                  onClick={handleReset}
                />
              )}

              <ProgressButton
                as='submit'
                isLoading={isLoading}
                disabled={!isDirty}
                className='btn btn-primary'
                label='common.confirm'
              />
            </div>
          </form>
        )}
      </LoadingLayout>
    </PageLayout>
  );
}

const updateGroupNicknameFormScheme = z.object({
  nickname: z.string().trim().max(15, 'errors.string.max-length|count=15'),
});

type UpdateGroupNicknameFormData = z.infer<typeof updateGroupNicknameFormScheme>;
